import { FunctionComponent, MouseEvent, useState } from "react";
import { Check, Copy } from "lucide-react";
import "./CopyButton.scss";

interface CopyButtonProps {
    /** Texto que vai para a area de transferencia */
    value: string;
    /** Tamanho do icone (default 14) */
    size?: number;
    /** Tempo em ms no estado "copiado" (default 1500) */
    copiedDuration?: number;
    /** title/aria-label do botao (default "Copiar") */
    title?: string;
    className?: string;
    onCopy?: (value: string) => void;
}

/**
 * Botao so-icone para copiar um valor (hash, endereco, etc.) com um clique.
 * Mostra um check por alguns ms apos copiar. Reusa o mesmo fallback do
 * CopyableInput para browsers sem a Clipboard API.
 */
const CopyButton: FunctionComponent<CopyButtonProps> = ({
    value,
    size = 14,
    copiedDuration = 1500,
    title = "Copiar",
    className = "",
    onCopy,
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(value);
        } catch {
            // fallback para browsers antigos / contextos sem Clipboard API
            const ta = document.createElement("textarea");
            ta.value = value;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch {}
            document.body.removeChild(ta);
        }
        setCopied(true);
        onCopy?.(value);
        setTimeout(() => setCopied(false), copiedDuration);
    };

    return (
        <button
            type="button"
            className={`ui-copy-btn ${copied ? "is-copied" : ""} ${className}`.trim()}
            onClick={handleCopy}
            aria-label={title}
            title={copied ? "Copiado!" : title}
        >
            {copied ? <Check size={size} /> : <Copy size={size} />}
        </button>
    );
};

export default CopyButton;
